import { useEffect, useMemo, useState } from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getBusinessAreasPaged,
  createBusinessArea,
  updateBusinessArea,
  deleteBusinessArea,
} from '../../../../../services/Businessareaservices';
import AreaCreateEditModal from './AreaCreateEditModal';
import ConfirmModal from '../../../../../components/ConfirmModal/ConfirmModal';
import AreaKeywordsModal from './AreaKeywordsModal';
import { getCurrentUser } from 'services/authService';
import { updateTribunalesArea } from '../../../../../services/roleoAutomaticoService';
import { useLanguage } from '../../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../../utils/createScopedI18n';
import { configAreasI18n } from './i18n';
import { useSnackbar } from 'context/SnackbarContext';

const FILTER_VALUES = {
  all: 'all',
  active: 'active',
  inactive: 'inactive',
};

const normalizeStatus = (value) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();

  if (['inactive', 'inactivo', 'inactivos'].includes(normalized)) {
    return FILTER_VALUES.inactive;
  }

  return FILTER_VALUES.active;
};

const normalizeFilterValue = (value) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();

  if (['all', 'todos'].includes(normalized)) return FILTER_VALUES.all;
  if (['active', 'activo', 'activos'].includes(normalized)) {
    return FILTER_VALUES.active;
  }
  if (['inactive', 'inactivo', 'inactivos'].includes(normalized)) {
    return FILTER_VALUES.inactive;
  }

  return null;
};

const toBackendStatusFilter = (value) => {
  switch (value) {
    case FILTER_VALUES.active:
      return 'Activos';
    case FILTER_VALUES.inactive:
      return 'Inactivos';
    default:
      return 'Todos';
  }
};

const mapBusinessArea = (area) => ({
  id: area.id,
  name: area.name || '',
  status: area.isEnabled ? FILTER_VALUES.active : FILTER_VALUES.inactive,
  courtCount: area.courtCount ?? 0,
});

export default function ConfigAreas() {
  const { language } = useLanguage();
  const areasLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: configAreasI18n });
  const { showSnackbar } = useSnackbar();

  const location = useLocation();
  const { DesignSystemComponents } = useOutletContext() ?? {};
  const { Button, Input, Table, Badge, IconButton, Modal, Dropdown, ComboBox } =
    DesignSystemComponents ?? {};

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [statusFilterValue, setStatusFilterValue] = useState(FILTER_VALUES.active);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [formValue, setFormValue] = useState({
    id: null,
    name: '',
    status: FILTER_VALUES.active,
  });
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [isKeywordsOpen, setIsKeywordsOpen] = useState(false);
  const [selectedArea, setSelectedArea] = useState(null);

  const [searchDebounced, setSearchDebounced] = useState('');
  const stateSearch = location.state?.search ?? '';
  const stateStatus = location.state?.status ?? '';

  useEffect(() => {
    configAreasI18n.changeLanguage(areasLanguage);
  }, [areasLanguage]);

  const statusOptions = useMemo(
    () => [
      { label: t('config_areas_filter_all'), value: FILTER_VALUES.all },
      { label: t('config_areas_filter_active'), value: FILTER_VALUES.active },
      {
        label: t('config_areas_filter_inactive'),
        value: FILTER_VALUES.inactive,
      },
    ],
    [t]
  );

  const selectedStatusFilter =
    statusOptions.find((option) => option.value === statusFilterValue) ||
    statusOptions[1];

  useEffect(() => {
    if (stateSearch) {
      setSearch(stateSearch);
      setPage(0);
    }
  }, [stateSearch]);

  useEffect(() => {
    if (!stateStatus) return;
    const normalized = normalizeFilterValue(stateStatus);
    if (normalized) {
      setStatusFilterValue(normalized);
      setPage(0);
    }
  }, [stateStatus]);

  const currentUserId = useMemo(() => {
    const currentUser = getCurrentUser?.();
    return (
      currentUser?.id ??
      currentUser?.userId ??
      currentUser?.usuarioAsignadoId ??
      null
    );
  }, []);

  const mapSortKeyToBackend = (key) => {
    switch (key) {
      case 'name':
        return 'name';
      case 'status':
        return 'isEnabled';
      default:
        return null;
    }
  };

  const handleServerSortChange = ({ sortKey: key, direction }) => {
    const normalizedDirection =
      typeof direction === 'string' ? direction.toLowerCase() : direction;
    setSortKey(normalizedDirection ? key : null);
    setSortDirection(normalizedDirection ?? null);
    setPage(0);
  };

  const backendSortKey = mapSortKeyToBackend(sortKey) || 'name';
  const backendSortDesc =
    sortDirection === null ? false : sortDirection === 'desc';

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(search);
      setPage(0);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const reloadBusinessAreas = async () => {
    const backendPage = page + 1;
    const response = await getBusinessAreasPaged(
      backendPage,
      pageSize,
      searchDebounced || null,
      toBackendStatusFilter(statusFilterValue),
      true,
      backendSortKey,
      backendSortDesc
    );

    const mappedAreas = (response.businessAreas || []).map(mapBusinessArea);

    setRows(mappedAreas);
    setTotal(response.total || 0);
  };

  useEffect(() => {
    let mounted = true;

    const fetchBusinessAreas = async () => {
      try {
        setLoading(true);

        const backendPage = page + 1;

        const response = await getBusinessAreasPaged(
          backendPage,
          pageSize,
          searchDebounced || null,
          toBackendStatusFilter(statusFilterValue),
          true,
          backendSortKey,
          backendSortDesc
        );

        if (!mounted) return;

        const mappedAreas = (response.businessAreas || []).map(mapBusinessArea);

        setRows(mappedAreas);
        setTotal(response.total || 0);
      } catch (error) {
        console.error('Error loading business areas:', error);
        if (mounted) {
          showSnackbar(error.customMessage || t('config_areas_error_load'), 'error');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchBusinessAreas();

    return () => {
      mounted = false;
    };
  }, [
    page,
    pageSize,
    searchDebounced,
    backendSortKey,
    backendSortDesc,
    statusFilterValue,
    showSnackbar,
    t,
  ]);

  const statusColor = (status) =>
    normalizeStatus(status) === FILTER_VALUES.active ? 'success' : 'light';

  const openCreate = () => {
    setFormMode('create');
    setFormValue({ id: null, name: '', status: FILTER_VALUES.active });
    setIsFormOpen(true);
  };

  const openEdit = (row) => {
    setFormMode('edit');
    setFormValue({ ...row, status: normalizeStatus(row?.status) });
    setIsFormOpen(true);
  };

  const openDelete = (row) => {
    setToDelete(row);
    setIsDeleteOpen(true);
  };

  const saveForm = async (updatedValue, tribunalIds = []) => {
    const dataToSave = updatedValue || formValue;

    if (!dataToSave.name.trim()) {
      showSnackbar(t('config_areas_required_fields'), 'error');
      return;
    }

    try {
      let createdId = dataToSave.id;
      if (formMode === 'create') {
        const created = await createBusinessArea({
          name: dataToSave.name,
        });

        createdId = created?.id ?? created?.businessAreaId ?? createdId;
        showSnackbar(t('config_areas_success_created'), 'success');
      } else {
        await updateBusinessArea(dataToSave.id, {
          name: dataToSave.name,
          isEnabled: normalizeStatus(dataToSave.status) === FILTER_VALUES.active,
        });

        showSnackbar(t('config_areas_success_updated'), 'success');
      }

      if (createdId) {
        if (formMode === 'edit') {
          await updateTribunalesArea(createdId, tribunalIds);
        } else if (tribunalIds.length > 0) {
          await updateTribunalesArea(createdId, tribunalIds);
        }
      }

      setIsFormOpen(false);
      await reloadBusinessAreas();
    } catch (error) {
      console.error('Error saving business area:', error);

      const errorMessage =
        error.response?.data?.Message ||
        error.response?.data?.message ||
        error.response?.data ||
        error.customMessage ||
        t('config_areas_error_save');

      showSnackbar(errorMessage, 'error');
    }
  };

  const confirmDelete = async () => {
    if (!toDelete?.id) return;

    try {
      await deleteBusinessArea(toDelete.id);

      setIsDeleteOpen(false);
      setToDelete(null);

      showSnackbar(t('config_areas_success_deleted'), 'success');

      await reloadBusinessAreas();
    } catch (error) {
      console.error('Error deleting business area:', error);

      const errorMessage =
        error.response?.data?.Message ||
        error.response?.data?.message ||
        error.response?.data ||
        error.customMessage ||
        t('config_areas_error_delete');

      showSnackbar(errorMessage, 'error');
    }
  };

  const handleOpenKeywords = (row) => {
    setSelectedArea(row);
    setIsKeywordsOpen(true);
  };

  if (!Button || !Input || !Table || !Badge || !IconButton || !Modal) {
    return null;
  }

  return (
    <div>
      <div className='row mb-3'>
        <div className='col-5'>
          <Input
            icon='search'
            name='searchArea'
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('config_areas_search_placeholder')}
            type='text'
            value={search}
          />
        </div>
        <div className='col-4'>
          <Dropdown
            options={statusOptions}
            value={selectedStatusFilter}
            onChange={(option) => {
              setStatusFilterValue(option?.value || FILTER_VALUES.all);
              setPage(0);
            }}
            placeholder={t('config_areas_filter_all')}
            width='100%'
            isDisabled={loading}
          />
        </div>
        <div className='col-3'>
          <div className='d-flex justify-content-end'>
            <Button
              label={t('config_areas_add_button')}
              size='sm'
              variant='primary'
              onClick={openCreate}
            />
          </div>
        </div>
      </div>

      <div className='table-container'>
        <Table
          columns={[
            { accessor: 'name', label: t('config_areas_col_name'), sort: true },
            {
              accessor: 'courtCount',
              label: t('config_areas_col_courts'),
              sort: true,
            },
            {
              accessor: 'status',
              label: t('config_areas_col_status'),
              sort: true,
              render: (row) => (
                <Badge
                  text={
                    normalizeStatus(row.status) === FILTER_VALUES.active
                      ? t('config_areas_status_active')
                      : t('config_areas_status_inactive')
                  }
                  color={statusColor(row.status)}
                  size='sm'
                />
              ),
            },
            {
              label: t('config_areas_col_actions'),
              render: (row) => (
                <div className='d-flex gap-2'>
                  <IconButton
                    ariaLabel={t('config_areas_action_edit')}
                    icon='pencil'
                    onClick={() => openEdit(row)}
                    size='md'
                    iconStroke='var(--primary-500)'
                  />
                  <IconButton
                    ariaLabel={t('config_areas_action_delete')}
                    icon='trash'
                    onClick={() => openDelete(row)}
                    size='md'
                    iconStroke='var(--error-500)'
                  />
                  <IconButton
                    ariaLabel={t('config_areas_action_keywords')}
                    icon='tag'
                    onClick={() => handleOpenKeywords(row)}
                    size='md'
                    iconStroke='var(--warning-500)'
                  />
                </div>
              ),
            },
          ]}
          data={rows}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(newSize) => {
            setPageSize(newSize);
            setPage(0);
          }}
          paginationMode='server'
          totalItems={total}
          paginator
          paginatorGroup={[
            { label: '10', value: 10 },
            { label: '20', value: 20 },
            { label: '50', value: 50 },
            { label: '100', value: 100 },
          ]}
          loading={loading}
          serverSortKey={sortKey}
          serverSortDirection={sortDirection}
          onServerSortChange={handleServerSortChange}
        />
        {!loading && rows.length === 0 && (
          <div className='text-center p-4'>
            <p className='caption font-light'>{t('config_areas_no_results')}</p>
          </div>
        )}
      </div>

      <AreaCreateEditModal
        Modal={Modal}
        Input={Input}
        Table={Table}
        ComboBox={ComboBox}
        show={isFormOpen}
        mode={formMode}
        value={formValue}
        Dropdown={Dropdown}
        onChange={setFormValue}
        onClose={() => setIsFormOpen(false)}
        onSave={saveForm}
        onRequestDelete={
          formMode === 'edit'
            ? () => {
                setIsFormOpen(false);
                setToDelete(formValue);
                setIsDeleteOpen(true);
              }
            : undefined
        }
      />

      <ConfirmModal
        Modal={Modal}
        show={isDeleteOpen}
        title={t('config_areas_confirm_delete_title')}
        message={
          toDelete
            ? t('config_areas_confirm_delete_message', { name: toDelete.name })
            : t('config_areas_confirm_delete_fallback')
        }
        confirmLabel={t('config_areas_confirm_delete_button')}
        cancelLabel={t('config_areas_cancel')}
        confirmVariant='danger'
        onConfirm={confirmDelete}
        onClose={() => {
          setIsDeleteOpen(false);
          setToDelete(null);
        }}
      />

      <AreaKeywordsModal
        DesignSystemComponents={DesignSystemComponents}
        Modal={Modal}
        show={isKeywordsOpen}
        area={selectedArea}
        userId={currentUserId}
        onClose={() => {
          setIsKeywordsOpen(false);
          setSelectedArea(null);
        }}
      />
    </div>
  );
}
